import { deriveTopic, makeBundle, type Envelope } from "@psplit/ledger-core";
import { repo } from "./repo.js";
import { errorMessage } from "./errors.js";

export interface SyncOutcome {
  ok: boolean;
  pushed: number;
  pulled: number;
  skipped: number; // envelopes that failed to decrypt/parse (foreign or corrupt)
  helloSent: boolean;
  error?: string;
}

const TIMEOUT_MS = 15_000;
const HELLO_THROTTLE_MS = 10 * 60_000;

// Auto-sync fires on boot AND on visibility changes; overlapping runs for the
// same group would race the cursor write. One sync per group at a time.
const inFlight = new Set<string>();

async function relayFetch(url: string, init?: RequestInit): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

// One full sync cycle for one group: push my delta, announce myself if I am
// not a member yet, pull and ingest everything new, then push anything
// authored DURING ingest (e.g. the auto-admit member_add answering a hello;
// dropping that step once caused permanent divergence). Safe to re-run any
// time; every step is idempotent on both ends.
export async function syncGroup(groupId: string): Promise<SyncOutcome> {
  const fail = (error: string): SyncOutcome => ({
    ok: false,
    pushed: 0,
    pulled: 0,
    skipped: 0,
    helloSent: false,
    error,
  });
  const meta = repo.meta(groupId);
  if (!meta) return fail("unknown group");
  if (meta.relayUrl === "") return fail("no relay configured");
  if (inFlight.has(groupId)) return fail("sync already in progress");
  inFlight.add(groupId);

  const topicFor = (key: Uint8Array) => `${meta.relayUrl.replace(/\/+$/, "")}/t/${deriveTopic(key)}`;
  let base = topicFor(repo.groupKey(groupId));
  const store = repo.storeOf(groupId);
  const st = await repo.loadSyncState(groupId);
  // A rotation moves the group to a new mailbox, so a cursor from the old one
  // means nothing. Start that topic from the beginning; ingest is idempotent.
  let cursor = (st.epoch ?? 0) === repo.epoch(groupId) ? st.cursor : 0;
  let pushed = 0;
  let helloSent = false;

  const postEnvelope = async (envelope: Envelope, label: string, to: string = base) => {
    const res = await relayFetch(to, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ e: envelope }),
    });
    if (!res.ok) throw new Error(`relay ${label} failed: ${res.status}`);
  };

  try {
    // 1. push what the relay has not seen from us
    const delta = makeBundle(store, st.lastPushHeads).ops;
    if (delta.length > 0) {
      await postEnvelope(await repo.encryptOpsEnvelope(groupId, delta), "push");
      pushed = delta.length;
    }
    const headsAfterPush = store.heads();

    // 2. joiner announces itself until admitted (throttled: pending groups
    // auto-sync often, and every hello is a stored envelope on the relay)
    if (
      repo.identity &&
      repo.membership(groupId) === "pending" &&
      Date.now() - (st.lastHelloAt ?? 0) > HELLO_THROTTLE_MS
    ) {
      await postEnvelope(await repo.buildHelloEnvelope(groupId), "hello");
      helloSent = true;
    }

    // 3. pull everything after our cursor. If what we ingest turns out to
    // contain a rotation addressed to us, the group has moved to a different
    // mailbox and we have to go and read that one too, hence the loop. Bounded
    // by the number of epochs we can actually catch up through in one sync.
    let pulled = 0;
    let skipped = 0;
    let latest = cursor;
    let topicMoved = false;
    const pulledHashes = new Set<string>();
    for (let hop = 0; hop < 8; hop++) {
      const epochBefore = repo.epoch(groupId);
      const res = await relayFetch(`${base}?since=${cursor}`);
      if (!res.ok) throw new Error(`relay pull failed: ${res.status}`);
      const data = (await res.json()) as {
        items: Array<{ seq: number; e: Envelope }>;
        latest: number;
      };
      for (const item of data.items) {
        try {
          const outcome = await repo.ingestEnvelope(groupId, item.e);
          pulled += outcome.added;
          for (const hash of outcome.acceptedHashes) pulledHashes.add(hash);
        } catch {
          skipped++; // not ours / corrupt - the ledger's own validation is the real gate
        }
      }
      latest = data.latest;
      if (repo.epoch(groupId) === epochBefore) break;
      // We were behind. Follow the group to its current mailbox and read it
      // from the start, since we have never pulled that topic before.
      topicMoved = true;
      base = topicFor(repo.groupKey(groupId));
      cursor = 0;
      // If the loop ends here (hop budget), the saved cursor must be the new
      // topic's, not the old one's - otherwise the next sync skips envelopes.
      latest = 0;
    }
    const data = { latest };

    // 4. push ops the CURRENT mailbox has not seen. Normally that is just what
    // was authored during ingest (e.g. the auto-admit member_add for a hello) -
    // without this they would land inside lastPushHeads' closure and never
    // reach the relay; the joiner would never learn it was admitted. If a
    // rotation moved the topic mid-cycle, it ALSO covers the step-1 delta:
    // that went to a mailbox the group has left, where nobody will ever read
    // it, so it counts as unpushed here. Ops merely pulled are excluded; the
    // relay already has them.
    const tailBase = topicMoved ? st.lastPushHeads : headsAfterPush;
    const tail = makeBundle(store, tailBase).ops.filter((op) => !pulledHashes.has(op.hash));
    if (tail.length > 0) {
      await postEnvelope(await repo.encryptOpsEnvelope(groupId, tail), "push");
      pushed += tail.length;
    }

    await repo.saveSyncState({
      groupId,
      cursor: data.latest,
      epoch: repo.epoch(groupId),
      lastPushHeads: store.heads(),
      lastSyncAt: Date.now(),
      lastHelloAt: helloSent ? Date.now() : (st.lastHelloAt ?? 0),
    });
    return { ok: true, pushed, pulled, skipped, helloSent };
  } catch (e) {
    return { ...fail(errorMessage(e)), pushed, helloSent };
  } finally {
    inFlight.delete(groupId);
  }
}

/**
 * Post the rotation to the mailbox the group just left. A member who was
 * offline through the rotation is still watching the old topic, and this is the
 * only place they can learn the key moved. Whoever was removed can read this
 * bundle, but the wrapped keys in it are not addressed to them, so it tells
 * them a rotation happened and nothing more.
 */
export async function announceRotation(groupId: string, previousKey: Uint8Array): Promise<void> {
  const meta = repo.meta(groupId);
  if (!meta || meta.relayUrl === "") return;
  const store = repo.storeOf(groupId);
  const st = await repo.loadSyncState(groupId);
  const ops = makeBundle(store, st.lastPushHeads).ops;
  if (ops.length === 0) return;
  const envelope = await repo.encryptOpsEnvelopeWith(previousKey, groupId, ops);
  const base = `${meta.relayUrl.replace(/\/+$/, "")}/t/${deriveTopic(previousKey)}`;
  const res = await relayFetch(base, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ e: envelope }),
  });
  if (!res.ok) throw new Error(`rotation announce failed: ${res.status}`);
}

// Groups sync against independent mailboxes; run them concurrently so total
// latency is the slowest group, not the sum.
export async function syncAllGroups(): Promise<void> {
  await Promise.allSettled(
    repo
      .groups()
      .filter((meta) => meta.relayUrl !== "")
      .map((meta) => syncGroup(meta.groupId)),
  );
}
